import { Box } from "@mui/material"
import { AddOutlined, FilterAltOutlined } from "@mui-symbols-material/w300"
import { useNavigate } from "@tanstack/react-router"
import type { ChangeEvent, FC } from "react"
import { useState } from "react"

import { TransferActionType } from "../../constants"
import { useAdvanceTransfer } from "../../hooks/useAdvanceTransfer"
import { useCancelTransfer } from "../../hooks/useCancelTransfer"
import { useConfirmTransferWorkOrder } from "../../hooks/useConfirmTransferWorkOrder"
import { useTransferFilterOptions } from "../../hooks/useTransferFilterOptions"
import { useTransferList } from "../../hooks/useTransferList"
import { useTransferPalletLabelPrinting } from "../../hooks/useTransferPalletLabelPrinting"
import type { TransferAction, TransferPalletWithProduct } from "../../types"
import { TransferDocumentsDialog } from "../TransferDocumentsDialog"
import { TransferLaidDownCostFormDialog } from "../TransferLaidDownCostFormDialog"
import { TransferLaunchFormDialog } from "../TransferLaunchFormDialog"
import { TransferLpnFormDialog } from "../TransferLpnFormDialog"
import { TransferPickTicketPrintView } from "../TransferPickTicketPrintView"
import { TransferReceiveFormDialog } from "../TransferReceiveFormDialog"
import { TransferWorkOrderPrintView } from "../TransferWorkOrderPrintView"

import { TransferFilterChips } from "./TransferFilterChips"
import { TransferFilterDrawer } from "./TransferFilterDrawer"
import { TransferTable } from "./TransferTable"

import { Button } from "@/components/common/Button"
import ConfirmationDialog from "@/components/common/Modals/ConfirmationDialog"
import SearchInput from "@/components/SearchInput/SearchInput"
import type { Transfer } from "@/graphql/codegen/graphql"
import { useWorkOrderPrint } from "@/screens/Inventory/hooks"

export const TransferListPage: FC = () => {
  const navigate = useNavigate()
  const [isLaunchOpen, setIsLaunchOpen] = useState(false)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [activeAction, setActiveAction] = useState<TransferAction | null>(null)

  const { printPalletLabels, isPrinting } = useTransferPalletLabelPrinting()
  const { cancelTransfer, isCancelling } = useCancelTransfer()
  const { confirmTransferWorkOrder } = useConfirmTransferWorkOrder()
  const { advanceTransfer } = useAdvanceTransfer()

  const { printViewRef: workOrderPrintViewRef, printWorkOrder } = useWorkOrderPrint("Transfer Work Order")

  const { printViewRef: pickTicketPrintViewRef, printWorkOrder: printPickTicket } =
    useWorkOrderPrint("Transfer Pick Ticket")

  const handleWorkOrderPrint = (transfer: Transfer) =>
    printWorkOrder(() => setActiveAction({ type: TransferActionType.WorkOrderPrint, transfer }))

  const handlePickTicketPrint = (transfer: Transfer) =>
    printPickTicket(() => setActiveAction({ type: TransferActionType.PickTicketPrint, transfer }))

  const handlePalletLabelPrint = (pallet: TransferPalletWithProduct) => printPalletLabels([pallet])

  const handlePalletLabelPrintAll = (transfer: Transfer) =>
    printPalletLabels(
      transfer.products.flatMap(({ product, pallets }) => pallets.map((pallet) => ({ ...pallet, product })))
    )

  const {
    transfers,
    isFetching,
    error,
    sorting,
    expanded,
    searchParams,
    tableContainerRef,
    refetch,
    handleScroll,
    handleSortingChange,
    handleExpandedChange,
    handleSearchChange,
    handleFilterChange,
    handleFilterRemove,
    handleFilterClear,
  } = useTransferList()

  const filterOptions = useTransferFilterOptions()

  const handleTransferEdit = (transfer: Transfer) =>
    navigate({ to: "/app/inventory/transfers/$transferId/edit", params: { transferId: transfer.transferId } })

  const handleActionClose = () => setActiveAction(null)

  const handleActionSuccess = () => {
    refetch()
    handleActionClose()
  }

  const handleTransferCancelConfirm = async () => {
    if (activeAction?.type !== TransferActionType.Cancel) return

    const isSuccess = await cancelTransfer(activeAction.transfer.transferId)

    if (isSuccess) handleActionSuccess()
  }

  const handleWorkOrderConfirm = async (transfer: Transfer) => {
    const isSuccess = await confirmTransferWorkOrder(transfer.transferId)

    if (isSuccess) refetch()
  }

  const handleTransferAdvance = async (transfer: Transfer) => {
    const isSuccess = await advanceTransfer(transfer.transferId, transfer.status)

    if (isSuccess) refetch()
  }

  return (
    <div className='flex h-full flex-col gap-y-6'>
      <div className='flex items-center gap-x-4'>
        <SearchInput
          placeholder='Search by Transfer #, Product ID, Description or Reference #'
          defaultValue={searchParams.query}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
          className='w-full max-w-[524px]'
          data-testid='transfer.list-page.search-field'
        />
        <Button
          appearance='outlined'
          startIcon={<FilterAltOutlined />}
          onClick={() => setIsFilterOpen(true)}
          data-testid='transfer.list-page.filter-button'
        >
          Filter
        </Button>
        <Button
          appearance='contained'
          startIcon={<AddOutlined />}
          className='ml-auto'
          onClick={() => setIsLaunchOpen(true)}
          data-testid='transfer.list-page.new-transfer-button'
        >
          New Transfer
        </Button>
      </div>

      <TransferFilterChips {...filterOptions} searchParams={searchParams} onRemove={handleFilterRemove} />

      <Box className='flex min-h-0 flex-1 overflow-y-auto'>
        <TransferTable
          data={transfers}
          loading={isFetching}
          error={error}
          sorting={sorting}
          expanded={expanded}
          isPrinting={isPrinting}
          containerRef={tableContainerRef}
          onScroll={handleScroll}
          onSortingChange={handleSortingChange}
          onExpandedChange={handleExpandedChange}
          onEdit={handleTransferEdit}
          onLpnEdit={(transfer) => setActiveAction({ type: TransferActionType.LpnEdit, transfer })}
          onLaidDownCostEdit={(transfer) => setActiveAction({ type: TransferActionType.LaidDownCostEdit, transfer })}
          onCancel={(transfer) => setActiveAction({ type: TransferActionType.Cancel, transfer })}
          onReceivedConfirm={(transfer) => setActiveAction({ type: TransferActionType.ConfirmReceived, transfer })}
          onWorkOrderConfirm={handleWorkOrderConfirm}
          onWorkOrderPrint={handleWorkOrderPrint}
          onPickTicketPrint={handlePickTicketPrint}
          onPickConfirm={handleTransferAdvance}
          onPalletLabelPrint={handlePalletLabelPrint}
          onPalletLabelPrintAll={handlePalletLabelPrintAll}
          onDocumentsOpen={(transfer) => setActiveAction({ type: TransferActionType.DocumentsView, transfer })}
        />
      </Box>
      <TransferFilterDrawer
        {...filterOptions}
        open={isFilterOpen}
        searchParams={searchParams}
        onToggle={() => setIsFilterOpen(false)}
        onChange={handleFilterChange}
        onClear={handleFilterClear}
      />
      {isLaunchOpen && <TransferLaunchFormDialog onClose={() => setIsLaunchOpen(false)} />}
      {activeAction?.type === TransferActionType.WorkOrderPrint && (
        <TransferWorkOrderPrintView ref={workOrderPrintViewRef} transfer={activeAction.transfer} />
      )}
      {activeAction?.type === TransferActionType.PickTicketPrint && (
        <TransferPickTicketPrintView ref={pickTicketPrintViewRef} transfer={activeAction.transfer} />
      )}
      {activeAction?.type === TransferActionType.DocumentsView && (
        <TransferDocumentsDialog transfer={activeAction.transfer} onClose={handleActionClose} />
      )}
      {activeAction?.type === TransferActionType.LpnEdit && (
        <TransferLpnFormDialog
          transfer={activeAction.transfer}
          onClose={handleActionClose}
          onSubmit={handleActionSuccess}
        />
      )}
      {activeAction?.type === TransferActionType.LaidDownCostEdit && (
        <TransferLaidDownCostFormDialog
          transfer={activeAction.transfer}
          onClose={handleActionClose}
          onSubmit={handleActionSuccess}
        />
      )}
      {activeAction?.type === TransferActionType.ConfirmReceived && (
        <TransferReceiveFormDialog
          transfer={activeAction.transfer}
          onClose={handleActionClose}
          onSubmit={handleActionSuccess}
        />
      )}
      {activeAction?.type === TransferActionType.Cancel && (
        <ConfirmationDialog
          title='Cancel Transfer'
          content='Are you sure you want to cancel this transfer?'
          closeLabel='Keep Transfer'
          confirmLabel='Cancel Transfer'
          confirmDisabled={isCancelling}
          onClose={handleActionClose}
          onConfirm={handleTransferCancelConfirm}
        />
      )}
    </div>
  )
}
